/**
 * RunRequirementsDialog Component
 *
 * Shows what requirements are missing before a Run can be executed.
 * Always allows user to click Run, but shows this dialog if something is missing.
 */

import { Button, Modal } from '@affine/component';
import { useI18n } from '@affine/i18n';
import {
  CheckBoxCheckLinearIcon,
  CloseIcon,
  InformationIcon,
} from '@blocksuite/icons/rc';
import { useTheme } from 'next-themes';
import { type FC, useMemo } from 'react';

export interface RunRequirement {
  id: string;
  label: string;
  met: boolean;
  description?: string;
}

// Blue-white theme dark colors
const BLUE_WHITE_COLORS = {
  // Info section (blue accent)
  infoBg: '#0A1929',
  infoAccent: '#1E96EB',
  // Warning section (orange accent)
  warningBg: '#1a1a0f',
  warningAccent: '#FF8F1F',
  // Success section (green accent)
  successBg: '#0f1a14',
  successAccent: '#10CB75',
  // Error section (red accent)
  errorBg: '#1a0f0f',
  errorAccent: '#EB4335',
  // Text
  textPrimary: '#FFFFFF',
  textSecondary: '#B2BAC2',
};

interface RunRequirementsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  requirements: RunRequirement[];
}

export const RunRequirementsDialog: FC<RunRequirementsDialogProps> = ({
  open,
  onOpenChange,
  requirements,
}) => {
  const t = useI18n();
  const { resolvedTheme } = useTheme();
  const isBlueWhite = resolvedTheme === 'blue-white';

  // Theme-aware colors
  const colors = useMemo(
    () => ({
      // Warning/Info section
      warningBg: isBlueWhite
        ? BLUE_WHITE_COLORS.warningBg
        : 'var(--streamlain-background-warning-color)',
      warningAccent: isBlueWhite
        ? BLUE_WHITE_COLORS.warningAccent
        : 'var(--streamlain-warning-color)',
      // Error section
      errorBg: isBlueWhite
        ? BLUE_WHITE_COLORS.errorBg
        : 'var(--streamlain-background-error-color)',
      errorAccent: isBlueWhite
        ? BLUE_WHITE_COLORS.errorAccent
        : 'var(--streamlain-error-color)',
      // Success section
      successBg: isBlueWhite
        ? BLUE_WHITE_COLORS.successBg
        : 'var(--streamlain-background-success-color)',
      successAccent: isBlueWhite
        ? BLUE_WHITE_COLORS.successAccent
        : 'var(--streamlain-success-color)',
      // Text
      textPrimary: isBlueWhite
        ? BLUE_WHITE_COLORS.textPrimary
        : 'var(--streamlain-text-primary-color)',
      textSecondary: isBlueWhite
        ? BLUE_WHITE_COLORS.textSecondary
        : 'var(--streamlain-text-secondary-color)',
      // Neutral background
      neutralBg: isBlueWhite
        ? 'rgba(255, 255, 255, 0.05)'
        : 'var(--streamlain-background-secondary-color)',
    }),
    [isBlueWhite]
  );

  const unmetRequirements = requirements.filter(r => !r.met);
  const metRequirements = requirements.filter(r => r.met);

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Requirements Not Met"
      width={480}
    >
      <div
        style={{
          padding: '16px 0',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        {/* Info Text */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            padding: '12px',
            backgroundColor: colors.warningBg,
            borderRadius: '8px',
          }}
        >
          <InformationIcon
            style={{
              width: '20px',
              height: '20px',
              color: colors.warningAccent,
              flexShrink: 0,
              marginTop: '2px',
            }}
          />
          <span
            style={{
              fontSize: '13px',
              lineHeight: 1.5,
              color: colors.textSecondary,
            }}
          >
            The following requirements must be met before running:
          </span>
        </div>

        {/* Requirements List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {/* Unmet requirements first */}
          {unmetRequirements.map(req => (
            <div
              key={req.id}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                padding: '12px',
                backgroundColor: colors.errorBg,
                borderRadius: '8px',
                border: `1px solid ${colors.errorAccent}`,
              }}
            >
              <CloseIcon
                style={{
                  width: '18px',
                  height: '18px',
                  color: colors.errorAccent,
                  flexShrink: 0,
                  marginTop: '1px',
                }}
              />
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: colors.errorAccent,
                    lineHeight: 1.4,
                  }}
                >
                  {req.label}
                </div>
                {req.description && (
                  <div
                    style={{
                      fontSize: '13px',
                      color: colors.textSecondary,
                      marginTop: '4px',
                      lineHeight: 1.5,
                    }}
                  >
                    {req.description}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Met requirements */}
          {metRequirements.map(req => (
            <div
              key={req.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px',
                backgroundColor: colors.neutralBg,
                borderRadius: '8px',
              }}
            >
              <CheckBoxCheckLinearIcon
                style={{
                  width: '18px',
                  height: '18px',
                  color: colors.successAccent,
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontSize: '14px',
                  color: colors.textSecondary,
                }}
              >
                {req.label}
              </span>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
            marginTop: '8px',
          }}
        >
          <Button onClick={() => onOpenChange(false)}>
            {t['com.streamlain.streamlain.common.close']()}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
